<template>
  <div class="pycad-slice-strip">
    <div class="pycad-slice-strip__accent" :style="{ background: color }"></div>
    <div class="pycad-slice-strip__body">
      <span class="pycad-slice-strip__axis" :style="{ color }">{{ axis }}</span>
      <div
        ref="trackEl"
        class="pycad-slice-strip__track"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerEnd"
        @pointercancel="onPointerEnd"
      >
        <div class="pycad-slice-strip__rail"></div>
        <div
          class="pycad-slice-strip__thumb"
          :style="{
            left: `${thumbPct}%`,
            background: color,
            boxShadow: `0 0 6px ${color}80`,
          }"
        ></div>
      </div>
      <div class="pycad-slice-strip__counter">
        <span class="pycad-slice-strip__current" :style="{ color }">
          {{ displayedValue + 1 }}
        </span>
        <span class="pycad-slice-strip__sep">/</span>
        <span class="pycad-slice-strip__total">{{ max + 1 }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

const props = defineProps<{
  modelValue: number;
  min: number;
  max: number;
  axis: string;
  color: string;
}>();

const emit = defineEmits<{ (e: 'update:modelValue', v: number): void }>();

const trackEl = ref<HTMLDivElement | null>(null);

const range = computed(() => Math.max(1, (props.max ?? 0) - (props.min ?? 0)));

const displayedValue = computed(() =>
  Math.max(props.min, Math.min(props.max, props.modelValue ?? props.min))
);

const thumbPct = computed(
  () => ((displayedValue.value - props.min) / range.value) * 100
);

function valueFromClientX(clientX: number): number {
  const el = trackEl.value;
  if (!el) return props.modelValue;
  const rect = el.getBoundingClientRect();
  const rel = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  const v = Math.round(props.min + rel * range.value);
  return Math.max(props.min, Math.min(props.max, v));
}

function onPointerDown(e: PointerEvent) {
  if (!trackEl.value) return;
  trackEl.value.setPointerCapture(e.pointerId);
  e.preventDefault();
  emit('update:modelValue', valueFromClientX(e.clientX));
}

function onPointerMove(e: PointerEvent) {
  if (!trackEl.value?.hasPointerCapture(e.pointerId)) return;
  e.preventDefault();
  emit('update:modelValue', valueFromClientX(e.clientX));
}

function onPointerEnd(e: PointerEvent) {
  if (!trackEl.value?.hasPointerCapture(e.pointerId)) return;
  e.preventDefault();
  trackEl.value.releasePointerCapture(e.pointerId);
}
</script>

<style scoped>
.pycad-slice-strip {
  display: flex;
  flex-direction: column;
  height: 28px;
  flex-shrink: 0;
  user-select: none;
}

.pycad-slice-strip__accent {
  height: 2px;
  width: 100%;
  opacity: 0.6;
  flex-shrink: 0;
}

.pycad-slice-strip__body {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  flex: 1;
  background: #050505;
}

.pycad-slice-strip__axis {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  width: 18px;
  flex-shrink: 0;
}

.pycad-slice-strip__track {
  position: relative;
  flex: 1;
  min-width: 0;
  height: 16px;
  cursor: pointer;
  touch-action: none;
}

.pycad-slice-strip__rail {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 2px;
  margin-top: -1px;
  background: #2c2c2c;
  border-radius: 1px;
}

.pycad-slice-strip__thumb {
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: box-shadow 0.15s;
}

.pycad-slice-strip__counter {
  display: flex;
  align-items: baseline;
  gap: 2px;
  flex-shrink: 0;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 11px;
  min-width: 52px;
  justify-content: flex-end;
}

.pycad-slice-strip__current {
  font-weight: 600;
}

.pycad-slice-strip__sep {
  color: #404040;
  font-size: 10px;
}

.pycad-slice-strip__total {
  color: #666666;
  font-size: 10px;
}
</style>
